import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { GetCredentialsOptions } from 'aws-amplify/auth';
import { get, post, put, patch, del, head } from 'aws-amplify/api';
import { downloadData, getUrl, list, uploadData } from 'aws-amplify/storage';
import { Cache } from 'aws-amplify/utils';
import { JwtPayload } from 'jwt-decode';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '@env/environment';
import { lastValueFrom } from 'rxjs';
import jwtDecode from 'jwt-decode';
import { CognitoServiceService } from './cognito/cognito-service.service';
import { ApiPayload } from '@common/models/api.model';

@Injectable({
  providedIn: 'root'
})
export class AmplifyService {
  userObj;
  amplifyConfig;
  constructor(
    public oidcSecurityService: OidcSecurityService,
    private readonly cognitoService: CognitoServiceService
  ) {
    this.amplifyConfig = {
      API: {
        REST: {
          GUI_IPerlDashboard: {
            endpoint: environment.GUI_IPerlDashboard,
            region: 'us-east-1'
          },
          GUI_DT96Dashboard: {
            endpoint: environment.GUI_DT96Dashboard,
            region: 'us-east-1'
          },
          GUI_DataEntryTool: {
            endpoint: environment.GUI_DataEntryTool,
            region: 'us-east-1'
          },
          GUI_Kepler: {
            endpoint: environment.KeplerDashboard,
            region: 'us-east-1'
          },
          GUI_Lifecycleservices: {
            endpoint: environment.GUI_Lifecycleservices,
            region: 'us-east-1'
          },
          repair_economy: {
            endpoint: environment.repair_economy,
            region: 'us-east-1'
          },
          pipelineData: {
            endpoint: environment.pipelineData,
            region: 'us-east-1'
          },
          gsoAPI: {
            endpoint: environment.gsoAPI,
            region: 'us-east-1'
          },
          'xsprout-scheduler': {
            endpoint: environment.schedulerApi,
            region: 'us-east-1'
          },
          dataImportApi: {
            endpoint: environment.dataImportApi,
            region: 'us-east-1'
          },
          genAIAPI: {
            endpoint: environment.genAIAPI,
            region: 'us-east-1'
          },
          backspoolExplorer: {
            endpoint: environment.backspoolExplorer,
            region: 'us-east-1'
          },
          'gui-cache': {
            endpoint: environment.guiCacheAPI,
            region: 'us-east-1'
          },
          'gui-reports': {
            endpoint: environment.guiReportsAPI,
            region: 'us-east-1'
          },
          'gui-athena-query': {
            endpoint: environment.guiAthenaQueryAPI,
            region: 'us-east-1'
          },
          'gui-meter-reads-data': {
            endpoint: environment.guiMeterReadsDataAPI,
            region: 'us-east-1'
          },
          'gui-ally-sales-tool-data-get': {
            endpoint: environment.guiAllySalesToolDataAPI,
            region: 'us-east-1'
          },
          'gui-ingest-status': {
            endpoint: environment.guiIngestStatusAPI,
            region: 'us-east-1'
          },
          'gui-api-key': {
            endpoint: environment.guiApiKeyAPI,
            region: 'us-east-1'
          }
        }
      },
      Storage: {
        S3: {
          bucket: environment.xylemDataLakeGUI,
          region: 'us-east-1',
          buckets: {
            xylemDataLakeGUI: {
              bucketName: environment.xylemDataLakeGUI,
              region: 'us-east-1'
            },
            dataImportBucket: {
              bucketName: environment.dataImportBucket,
              region: 'us-east-1'
            },
            backspoolBucket: {
              bucketName: environment.backspoolBucket,
              region: 'us-east-1'
            },
            piperdiverBucket: {
              bucketName: environment.piperdiverBucket,
              region: 'us-east-1'
            },
            backspoolViewerBucket: {
              bucketName: environment.backspoolViewerBucket,
              region: 'us-east-1'
            }
          }
        }
      }
    };
    // configure amplify
    Amplify.configure(this.amplifyConfig, {
      Auth: {
        credentialsProvider: this.cognitoService
      },
      API: {
        REST: {
          retryStrategy: {
            strategy: 'no-retry'
          }
        }
      }
    });
  }
  async callAPI(
    httpMethod: string,
    apiName: string,
    path: string,
    init?: ApiPayload
  ): Promise<any> {
    let response: any = null;
    try {
      const options = {
        ...init
      };

      switch (httpMethod) {
        case 'get':
          response = await get({ apiName, path, options }).response;
          break;

        case 'head':
          response = await head({ apiName, path, options }).response;
          break;

        case 'patch':
          response = await patch({ apiName, path, options }).response;
          break;

        case 'post':
          response = await post({ apiName, path, options }).response;
          break;

        case 'put':
          response = await put({ apiName, path, options }).response;
          break;

        case 'delete':
          response = await del({ apiName, path, options }).response;
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${httpMethod}`);
      }
      if (response && response.body) {
        response = await response.body.json();
      }
    } catch (error) {
      console.log(`error: ${error}`);
      throw error;
    }

    return response;
  }
  async getFileFromS3(key: string, bucket?: string): Promise<any> {
    let data = {};

    try {
      data = await getUrl({
        key,
        options: {
          bucket: bucket ? bucket : environment.xylemDataLakeGUI
        }
      });
    } catch (error) {
      console.log(`error: ${error}`);
    }

    return data;
  }

  async getS3FileText(filePath: string, bucket?: string): Promise<string> {
    const urlResult = await getUrl({
      path: filePath,
      options: {
        bucket: bucket ? { bucketName: bucket, region: 'us-east-1' } : undefined
      }
    });
    const response = await fetch(urlResult.url.toString());
    return response.text();
  }

  async listFileFromS3(key: string): Promise<any> {
    let data = {};

    try {
      data = await list({ prefix: key });
    } catch (error) {
      console.log(`error: ${error}`);
    }

    return data;
  }

  /**
   * Signs in the user using OIDC and Cognito, and caches the federated info.
   * @returns A promise that resolves when the sign-in process is complete.
   * @throws Will log an error to the console if the sign-in process fails.
   */
  async signIn(): Promise<void> {
    try {
      const source$ = this.oidcSecurityService.getAccessToken();
      const token = await lastValueFrom(source$);

      const value = await this.cognitoService.getCredentialsAndIdentityId(
        {} as GetCredentialsOptions
      );
      await Cache.setItem('federatedInfo', value, {
        expires: jwtDecode<JwtPayload>(token).exp * 1000
      });
    } catch (error) {}
  }
  /**
   * Signs out the user from AWS.
   */
  async awsSignOut(): Promise<void> {
    try {
      await Cache.removeItem('federatedInfo');
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  }

  async uploadFileToS3(fileName, file: File, bucketName: string) {
    return uploadData({
      path: fileName,
      data: file,
      options: {
        bucket: bucketName,
        metadata: { user: localStorage.getItem('user') }
      }
    });
  }
  async downloadFileFromS3(
    fileName: string,
    bucketName?: string
  ): Promise<void> {
    try {
      const result = await downloadData({
        path: fileName,
        options: {
          bucket: bucketName
        }
      }).result;

      // Get the blob from the result
      const blob = await result.body.blob();

      // Create a blob URL
      const url = URL.createObjectURL(blob);

      // Create an anchor element and click it to start download
      const anchor = document.createElement('a');
      anchor.href = url;
      // Extract just the filename from the S3 path
      anchor.download = fileName;
      anchor.style.display = 'none';

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Clean up the blob URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }
}
